"use client"

import React, { HTMLAttributes, useCallback, useMemo, useState } from "react"
import Link, { type LinkProps } from "next/link"
import { APP_B2B_ACCOUNT_OWNER } from "@/constants"
import {
  BookMarked,
  Building2,
  ChevronDownIcon,
  ChevronUpIcon,
  FilePenLine,
  Home,
  MapPinHouse,
  Package,
  ShoppingCart,
  UserCog,
  type LucideIcon,
} from "lucide-react"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { hasAnyRole } from "@/lib/role-helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import { isSbLocaleDach } from "@/components/ui/helpers/utils"
import StoryblokLogoutButton from "@/components/storyblok/header/StoryblokLogoutButton"

interface AccountNavigationLinkProps extends LinkProps, HTMLAttributes<HTMLAnchorElement> {
  text?: string
  icon?: LucideIcon
  active?: boolean
  sbLocale?: string
  b2bOnly: boolean
  hidden: boolean
}

function AccountNavigationLink(props: AccountNavigationLinkProps) {
  return (
    <Link
      prefetch={false}
      href={getLinkWithPrefixFromURLAndLocale(props.href as string, props.sbLocale) as string}
      className={cn(
        "flex w-full items-center bg-card py-2.5 pl-3 pr-2.5 leading-7 hover:text-primary",
        {
          "bg-primary text-primary-foreground hover:text-primary-foreground hover:bg-primary/90": props.active,
        },
        props.className
      )}
    >
      {props.icon && <props.icon className={"mr-4 inline-block"} size={20} />} {props.text}
    </Link>
  )
}

interface AccountNavigationProps {
  activeMenuItem: string
  sbLocale: string
  isB2B: boolean
  storyBlokLabels: { [key: string]: string }
  accountContext: AccountContext
}

export default function AccountNavigationClient(props: AccountNavigationProps) {
  const accountNavigationLinks: AccountNavigationLinkProps[] = [
    {
      href: "/account/dashboard",
      // text: "account.nav.dashboard",
      id: "dashboard",
      icon: Home,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/orders",
      // text: "account.nav.orders",
      id: "orders",
      icon: Package,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/articles",
      // text: "account.nav.articles",
      id: "articles",
      icon: ShoppingCart,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/wishlists",
      // text: "account.nav.wishlists",
      id: "wishlists",
      icon: BookMarked,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/addresses",
      // text: "account.nav.addresses",
      id: "addresses",
      icon: MapPinHouse,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/profile",
      // text: "account.nav.profile",
      id: "profile",
      icon: Building2,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/quotes",
      // text: "account.nav.quotes",
      id: "quotes",
      icon: FilePenLine,
      b2bOnly: true,
      hidden: !isSbLocaleDach(props.sbLocale),
    },
    {
      href: "/account/users",
      // text: "account.nav.usermanagement",
      id: "usermanagement",
      icon: UserCog,
      b2bOnly: true,
      hidden: !hasAnyRole(props.accountContext, [APP_B2B_ACCOUNT_OWNER]),
    } /*,
  {
    href: "/account/subscriptions",
    text: "account.nav.subscriptions",
    id: "subscriptions",
    icon: Mails,
    b2bOnly: false,
  },*/,
  ]

  console.log("sbLocale", props.sbLocale)

  const [open, setOpen] = useState<boolean>(false)
  const toggle = useCallback((): void => setOpen((state) => !state), [])
  useMemo(() => {
    return accountNavigationLinks.find((link) => link.id === props.activeMenuItem)
  }, [props.activeMenuItem])
  return (
    <>
      <button
        className={"flex w-full items-center justify-between bg-white p-2.5 text-xl shadow md:hidden"}
        onClick={toggle}
      >
        {props.storyBlokLabels["label_menu"]}
        {open ? (
          <ChevronUpIcon className="ml-2 h-[16px] text-primary" />
        ) : (
          <ChevronDownIcon className="ml-2 h-[16px] text-primary" />
        )}
      </button>
      <div className={"relative max-h-full w-full md:min-w-72 md:max-w-80"}>
        <div
          className={cn(
            "absolute inset-x-0 z-50 max-h-full min-h-[calc(100vh-1rem)] w-full overflow-y-auto overflow-x-hidden bg-black/30 md:relative md:inset-0 md:inset-x-auto md:z-auto md:block md:max-h-none md:min-h-fit md:w-auto  md:rounded-sm md:border md:border-gray-200 md:bg-white ",
            {
              hidden: !open,
            }
          )}
          onClick={toggle}
        >
          <nav className={cn("m-auto w-[calc(100%-100px)] text-base font-bold md:block md:w-auto")}>
            {accountNavigationLinks.map((navigationLink) => (
              <div key={navigationLink.id}>
                {(props.isB2B || !navigationLink.b2bOnly) && !navigationLink.hidden && (
                  <AccountNavigationLink
                    sbLocale={props.sbLocale}
                    {...navigationLink}
                    active={navigationLink.id === props.activeMenuItem}
                    text={props.storyBlokLabels[`label_${navigationLink.id}`]}
                  />
                )}
              </div>
            ))}
            <div
              className={
                "flex w-full items-center bg-card pb-2.5 pl-3 pr-2.5 font-normal leading-7 hover:font-bold hover:text-primary"
              }
            >
              <StoryblokLogoutButton sbLocale={props.sbLocale} />
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}
